<script setup lang="ts">
import { computed, ref } from 'vue';

import type { VFormRef } from '@modules/forms/components/VForm/types';
import VButton from '@components/buttons/VButton/VButton.vue';
import VDialog from '@components/dialogs/VDialog/VDialog.vue';
import VForm from '@modules/forms/components/VForm/VForm.vue';

import type { ITrackerTrafficSourceComposableOptions, ITrackerTrafficSourceForm } from '../../types';
import type { IBulkChangingTrafficSourceEmits, IBulkChangingTrafficSourceProps } from './types';
import { useTrackerTrafficSource } from '../../composables';
import { TRANSLATION_GROUP_AFFILIATES_TRACKERS_SETTINGS_TRACKERS } from '../../constants';
import { useBulkChangeTrackerTrafficSourceMutation } from '../../mutations';

const {
  affId,
  selectedIds = [],
  filterId = 0,
  page = 1,
} = defineProps<IBulkChangingTrafficSourceProps>();

const emit = defineEmits<IBulkChangingTrafficSourceEmits>();

const visible = defineModel<boolean>('visible');

const form = ref<VFormRef>();

const {
  mutateAsync,
  isLoading,
  errors,
  clearError,
  clearAllErrors,
} = useBulkChangeTrackerTrafficSourceMutation();

const trafficSourceFormOptions = computed<ITrackerTrafficSourceComposableOptions>(() => ({
  affiliateId: affId,
  withoutDefault: true,
  enableQuery: visible.value,
  hideDepartment: true,
}));

const {
  formTrafficSourceSchema,
  formGroups,
  showTrafficSourceWarning,
  onChange,
} = useTrackerTrafficSource(trafficSourceFormOptions);

async function onSubmit({ traffic_source_id }: ITrackerTrafficSourceForm): Promise<void> {
  if (!traffic_source_id)
    return;

  await mutateAsync({
    id: affId,
    segment_id: filterId,
    page,
    tracker: {
      traffic_source_id,
      selected_tracker_ids: selectedIds,
    },
  });

  close();
}

function onFormSubmit(): void {
  form.value?.submit();
}

function onHide(): void {
  showTrafficSourceWarning.value = false;
  clearAllErrors();
  emit('hide');
}

function close(): void {
  visible.value = false;
}
</script>

<template>
  <VDialog
    v-model:visible="visible"
    :title="$getTranslation(TRANSLATION_GROUP_AFFILIATES_TRACKERS_SETTINGS_TRACKERS, 'BULK_CHANGING_TRAFFIC_SOURCE_TITLE')"
    @hide="onHide"
  >
    <VForm
      ref="form"
      :schema="formTrafficSourceSchema"
      :groups="formGroups"
      :is-loading="isLoading"
      :custom-errors="errors"
      @change="onChange"
      @submit="onSubmit"
      @interacted="clearError"
    />
    <template #footer>
      <VButton
        :label="$getTranslation(TRANSLATION_GROUP_AFFILIATES_TRACKERS_SETTINGS_TRACKERS, 'BULK_CHANGING_TRAFFIC_SOURCE_CANCEL')"
        outlined
        @click="close"
      />
      <VButton
        :label="$getTranslation(TRANSLATION_GROUP_AFFILIATES_TRACKERS_SETTINGS_TRACKERS, 'BULK_CHANGING_TRAFFIC_SOURCE_SAVE')"
        :loading="isLoading"
        @click="onFormSubmit"
      />
    </template>
  </VDialog>
</template>
